import React, { useState } from "react";
import { useGeneralMessage } from "../../../../../common/APICommon";
import { GeneralMessage } from "../../../../../common/APICommon";
import { SummarySection } from "../fields/ConfigurationTabFld/SummarySection";
import { ProgressBar } from "../fields/ConfigurationTabFld/ProgressBar";
import { CategoryForm } from "../fields/ConfigurationTabFld/CategoryForm";
import { CategorySection } from "../fields/ConfigurationTabFld/CategorySection";
import { EmptyState } from "../fields/ConfigurationTabFld/EmptyState";
import { getCategoryColor, getCategoryIcon } from "../fields/ConfigurationTabFld/utils/categoryUtils";
import { calculateObjectivesTotal } from "../fields/ConfigurationTabFld/utils/calculationUtils";

export function ConfigurationTab({
  form,
  setForm,
  newCategory,
  setNewCategory,
  editingCategory,
  setEditingCategory,
  addCategory,
  updateCategory,
  deleteCategory
}) {
  const [categoryObjectives, setCategoryObjectives] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  // State for editing objectives
  const [editingObjective, setEditingObjective] = useState(null);

  // State for validation errors
  const [categoryErrors, setCategoryErrors] = useState({
    description: false,
    totalPercentage: false
  });

  const [objectiveErrors, setObjectiveErrors] = useState({
    code: false,
    description: false,
    percentage: false
  });

  // Function to remove objective
  const removeObjective = (categoryId, objectiveId) => {
    setForm(prev => ({
      ...prev,
      performanceCategories: prev.performanceCategories.map(cat =>
        cat.id === categoryId
          ? {
            ...cat,
            objectives: (cat.objectives || []).filter(obj => obj.id !== objectiveId)
          }
          : cat
      )
    }));

    if (editingObjective && editingObjective.id === objectiveId) {
      setEditingObjective(null);
    }
  };

  // Function to start editing an objective
  const startEditObjective = (category, objective) => {
    setEditingObjective({
      ...objective,
      categoryId: category.id,
      originalPercentage: objective.percentage
    });
  };

  // Function to save edited objective
  const saveEditObjective = () => {
    if (!editingObjective) return;

    const errors = {
      code: !editingObjective.code,
      description: !editingObjective.description,
      percentage: !editingObjective.percentage || parseFloat(editingObjective.percentage) <= 0
    };

    setObjectiveErrors(errors);

    if (errors.code || errors.description || errors.percentage) {
      showGeneralMessage("Please fill all objective fields with valid values", false);
      return;
    }

    const category = form.performanceCategories.find(cat => cat.id === editingObjective.categoryId);
    if (!category) return;

    const objectivesTotal = calculateObjectivesTotal(category.objectives);
    const remainingPercentage = (parseFloat(category.totalPercentage) || 0) - objectivesTotal;
    const newPercentage = parseFloat(editingObjective.percentage);
    const originalPercentage = parseFloat(editingObjective.originalPercentage || 0);
    const actualRemaining = remainingPercentage + originalPercentage;

    if (newPercentage > actualRemaining) {
      showGeneralMessage(`Cannot update objective. Maximum available percentage is ${actualRemaining.toFixed(2)}%`, false);
      return;
    }

    setForm(prev => ({
      ...prev,
      performanceCategories: prev.performanceCategories.map(cat =>
        cat.id === editingObjective.categoryId
          ? {
            ...cat,
            objectives: (cat.objectives || []).map(obj =>
              obj.id === editingObjective.id
                ? {
                  ...obj,
                  code: editingObjective.code,
                  description: editingObjective.description,
                  percentage: newPercentage
                }
                : obj
            )
          }
          : cat
      )
    }));

    setEditingObjective(null);
    setObjectiveErrors({ code: false, description: false, percentage: false });
  };

  // Function to cancel editing an objective
  const cancelEditObjective = () => {
    setEditingObjective(null);
    setObjectiveErrors({ code: false, description: false, percentage: false });
  };

  // Handle objective field changes while editing
  const handleEditObjectiveFieldChange = (field, value) => {
    if (editingObjective) {
      setEditingObjective(prev => ({
        ...prev,
        [field]: value
      }));
    }

    if (objectiveErrors[field]) {
      setObjectiveErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Validate and add category
  const handleAddCategory = () => {
    const errors = {
      description: !newCategory.description,
      totalPercentage: !newCategory.totalPercentage || parseFloat(newCategory.totalPercentage) <= 0
    };

    setCategoryErrors(errors);

    if (errors.description || errors.totalPercentage) {
      if (newCategory.description === "" || newCategory.totalPercentage === "") return;
      showGeneralMessage("Please fill all category fields with valid values", false);
      return;
    }

    addCategory();
    setCategoryErrors({ description: false, totalPercentage: false });
  };

  // Validate and update category
  const handleUpdateCategory = () => {
    if (!editingCategory) return;

    const errors = {
      description: !editingCategory.description,
      totalPercentage: !editingCategory.totalPercentage || parseFloat(editingCategory.totalPercentage) <= 0
    };

    setCategoryErrors(errors);

    if (errors.description || errors.totalPercentage) {
      showGeneralMessage("Please fill all category fields with valid values", false);
      return;
    }

    updateCategory();
    setCategoryErrors({ description: false, totalPercentage: false });
  };

  // Validate and add objective to a specific category
  const handleAddObjective = (category) => {
    const objectiveData = categoryObjectives[category.id] || {
      code: "",
      description: "",
      percentage: ""
    };

    const errors = {
      code: !objectiveData.code,
      description: !objectiveData.description,
      percentage: !objectiveData.percentage || parseFloat(objectiveData.percentage) <= 0
    };

    setObjectiveErrors(errors);

    if (objectiveData.code === "" || objectiveData.description === "" || objectiveData.percentage === "") return;

    if (errors.code || errors.description || errors.percentage) {
      showGeneralMessage("Please fill all objective fields with valid values", false);
      return;
    }

    const objectivesTotal = calculateObjectivesTotal(category.objectives);
    const remainingPercentage = (parseFloat(category.totalPercentage) || 0) - objectivesTotal;
    const newPercentage = parseFloat(objectiveData.percentage);

    if (newPercentage > remainingPercentage) {
      showGeneralMessage(`Cannot add objective. Maximum available percentage is ${remainingPercentage.toFixed(2)}%`, false);
      return;
    }

    const objective = {
      id: Date.now(),
      performanceCategoriesId: category.id,
      code: objectiveData.code,
      description: objectiveData.description,
      percentage: newPercentage
    };

    setForm(prev => ({
      ...prev,
      performanceCategories: prev.performanceCategories.map(cat =>
        cat.id === category.id
          ? {
            ...cat,
            objectives: [...(cat.objectives || []), objective]
          }
          : cat
      )
    }));

    setCategoryObjectives(prev => ({
      ...prev,
      [category.id]: { code: "", description: "", percentage: "" }
    }));

    setObjectiveErrors({ code: false, description: false, percentage: false });
  };

  // Handle category field changes
  const handleCategoryFieldChange = (field, value) => {
    if (editingCategory) {
      setEditingCategory({ ...editingCategory, [field]: value });
    } else {
      setNewCategory({ ...newCategory, [field]: value });
    }

    if (categoryErrors[field]) {
      setCategoryErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  // Handle objective field changes
  const handleObjectiveFieldChange = (categoryId, field, value) => {
    setCategoryObjectives(prev => ({
      ...prev,
      [categoryId]: {
        ...(prev[categoryId] || { code: "", description: "", percentage: "" }),
        [field]: value
      }
    }));

    if (objectiveErrors[field]) {
      setObjectiveErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  // Calculate summary values
  const totalCategories = form.performanceCategories.length;
  const totalWeight = form.performanceCategories.reduce((sum, cat) => sum + (parseFloat(cat.totalPercentage) || 0), 0);
  const totalObjectives = form.performanceCategories.reduce((sum, cat) => sum + (cat.objectives?.length || 0), 0);

  // Handle edit functions
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryErrors({ description: false, totalPercentage: false });
  };

  const handleDeleteCategory = (categoryId) => {
    deleteCategory(categoryId);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Performance Configuration</h2>
        <p className="text-gray-600 mt-2">Manage performance categories and objectives</p>
        
        <ProgressBar totalWeight={totalWeight} />
      </div>

      <SummarySection
        totalCategories={totalCategories}
        totalWeight={totalWeight}
        totalObjectives={totalObjectives}
      />

      <CategoryForm
        editingCategory={editingCategory}
        newCategory={newCategory}
        categoryErrors={categoryErrors}
        onFieldChange={handleCategoryFieldChange}
        onAddCategory={handleAddCategory}
        onUpdateCategory={handleUpdateCategory}
        onCancelEdit={() => {
          setEditingCategory(null);
          setCategoryErrors({ description: false, totalPercentage: false });
        }}
      />

      <div className="overflow-hidden rounded-xl border border-gray-200 mb-8">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Category & Objectives
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Code
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Weight
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Manage
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {form.performanceCategories.length === 0 ? (
              <EmptyState />
            ) : (
              form.performanceCategories.map((category) => (
                <CategorySection
                  key={category.id}
                  category={category}
                  expanded={expandedCategories[category.id]}
                  editingObjective={editingObjective}
                  categoryObjectives={categoryObjectives}
                  objectiveErrors={objectiveErrors}
                  getCategoryIcon={getCategoryIcon}
                  getCategoryColor={getCategoryColor}
                  onToggleCategory={toggleCategory}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onAddObjective={handleAddObjective}
                  onObjectiveFieldChange={handleObjectiveFieldChange}
                  onStartEditObjective={startEditObjective}
                  onSaveEditObjective={saveEditObjective}
                  onCancelEditObjective={cancelEditObjective}
                  onRemoveObjective={removeObjective}
                  onEditObjectiveFieldChange={handleEditObjectiveFieldChange}
                  calculateObjectivesTotal={calculateObjectivesTotal}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />
    </div>
  );
}